// ═══════════════════════════════════════════════════════════════
// TMETA → Real Columns Migration Tool
// Reads every load ticket, extracts the TMETA blob from the notes
// field, and writes formerly-TMETA fields to their proper GAB columns.
// Safe to re-run — skips tickets where the real column already has data.
//
// Phase 1 (complete): truckNumber, pickupLat/Lng, subcontractorName,
//                     clinCode, clinDescription, distanceMiles,
//                     transactionRate, extendedCost, disposalSiteName,
//                     loadDate, roadName/Owner/Class, pickupAddress/Time
// Phase 2 (current):  crewId, loadPercentage, disposalTime, disposalDate,
//                     monitorFirmName, loadMonitorId, disposalMonitorId,
//                     disposalMonitorName, contractorName, roeNumber,
//                     truckCapacity, transactionQuantity, invoiceNumber,
//                     ticketType, unitSubcategory, scaleTicketNumber, qrCode
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { Title, Text, Button, Paper, Stack, Group, Badge } from '../../components/gab/GabComponents';
import { IconDatabaseImport, IconCircleCheck, IconAlertTriangle } from '@tabler/icons-react';

const TMETA_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;

function decodeMeta(notes: string): Record<string, string> {
  const m = notes.match(TMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}

interface MigrationResult {
  total: number;
  migrated: number;
  skipped: number;
  errors: number;
  log: string[];
}

export default function TmetaMigration() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [dryRun, setDryRun] = useState(true);

  const run = useCallback(async () => {
    setRunning(true);
    setResult(null);

    const log: string[] = [];
    let total = 0, migrated = 0, skipped = 0, errors = 0;

    try {
      const { queryRecords, updateRecord: updateRec } = await import('../../api/client');
      const { TABLES } = await import('../../api/tables');
      const { FIELDS } = await import('../../api/fields');
      const FT = FIELDS.debrisLoadTickets;

      const PAGE = 200;
      let offset = 0, grandTotal = Infinity;

      while (offset < grandTotal) {
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
          limit: PAGE, offset,
        });
        grandTotal = res.total;
        total = grandTotal;

        for (const row of res.records) {
          const id = Number(row.record_id);
          const rawNotes = String(row[FT.notes] ?? '');
          const meta = decodeMeta(rawNotes);

          const ALL_FIELDS = [
            { col: FT.truckNumber,        mk: 'truckNumber' },
            { col: FT.pickupLat,          mk: 'pickupLat' },
            { col: FT.pickupLng,          mk: 'pickupLng' },
            { col: FT.pickupAddress,      mk: 'pickupAddress' },
            { col: FT.pickupTime,         mk: 'pickupTime' },
            { col: FT.subcontractorName,  mk: 'subcontractorName' },
            { col: FT.clinCode,           mk: 'clinCode' },
            { col: FT.clinDescription,    mk: 'clinDescription' },
            { col: FT.distanceMiles,      mk: 'distanceMiles' },
            { col: FT.transactionRate,    mk: 'transactionRate' },
            { col: FT.extendedCost,       mk: 'extendedCost' },
            { col: FT.disposalSiteName,   mk: 'disposalSiteName' },
            { col: FT.loadDate,           mk: 'loadDate' },
            { col: FT.roadName,           mk: 'roadName' },
            { col: FT.roadOwner,          mk: 'roadOwner' },
            { col: FT.roadOwnerName,      mk: 'roadOwnerName' },
            { col: FT.roadClass,          mk: 'roadClass' },
            // Phase 2: TDMS + ops columns
            { col: FT.crewId,             mk: 'crewId' },
            { col: FT.loadPercentage,     mk: 'loadPercentage' },
            { col: FT.disposalTime,       mk: 'disposalTime' },
            { col: FT.disposalDate,       mk: 'disposalDate' },
            { col: FT.monitorFirmName,    mk: 'monitorFirmName' },
            { col: FT.loadMonitorId,      mk: 'loadMonitorId' },
            { col: FT.disposalMonitorId,  mk: 'disposalMonitorId' },
            { col: FT.disposalMonitorName,mk: 'disposalMonitorName' },
            { col: FT.contractorName,     mk: 'contractorName' },
            { col: FT.roeNumber,          mk: 'roeNumber' },
            { col: FT.truckCapacity,      mk: 'truckCapacity' },
            { col: FT.transactionQuantity,mk: 'transactionQuantity' },
            { col: FT.invoiceNumber,      mk: 'invoiceNumber' },
            { col: FT.ticketType,         mk: 'ticketType' },
            { col: FT.unitSubcategory,    mk: 'unitSubcategory' },
            { col: FT.scaleTicketNumber,  mk: 'scaleTicketNumber' },
            { col: FT.qrCode,             mk: 'qrCode' },
          ];

          const needsMigration = ALL_FIELDS.some(({ col, mk }) => {
            const colVal = row[col];
            const metaVal = meta[mk];
            return metaVal && (colVal === null || colVal === undefined || colVal === '');
          });

          if (!needsMigration) { skipped++; continue; }

          if (dryRun) {
            const ticketNum = String(row[FT.ticketNumber] ?? id);
            const fields = ALL_FIELDS.filter(({ mk }) => meta[mk]).map(({ mk }) => mk).join(', ');
            log.push(`[DRY RUN] Ticket ${ticketNum}: ${fields}`);
            migrated++;
            continue;
          }

          try {
            const payload: Record<string, unknown> = {};
            for (const { col, mk } of ALL_FIELDS) {
              if (meta[mk]) payload[col] = meta[mk];
            }
            // Special case: loadTime in TMETA maps to pickupTime column
            if (meta.loadTime && !meta.pickupTime) payload[FT.pickupTime] = meta.loadTime;
            await updateRec(TABLES.debrisLoadTickets, id, payload);
            migrated++;
          } catch (e) {
            errors++;
            log.push(`ERROR ticket ${id}: ${e instanceof Error ? e.message : String(e)}`);
          }
        }

        offset += PAGE;
        log.push(`Progress: ${Math.min(offset, grandTotal).toLocaleString()} / ${grandTotal.toLocaleString()}`);
      }
    } catch (e) {
      log.push(`FATAL: ${e instanceof Error ? e.message : String(e)}`);
    }

    setResult({ total, migrated, skipped, errors, log });
    setRunning(false);
  }, [dryRun]);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
      <Stack gap={24}>
        <div>
          <Title order={2} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <IconDatabaseImport size={28} color="#4B3FFF" /> TMETA → Column Migration
          </Title>
          <Text c="dimmed" size="sm">
            One-time migration. Reads every load ticket, extracts the TMETA JSON blob from the notes
            field, and writes the values to their proper GAB columns. After running, existing tickets
            will have their data in real queryable columns instead of the notes blob.
          </Text>
        </div>

        <Paper p="md" withBorder radius="md" style={{ background: '#fefce8', borderColor: '#fbbf24' }}>
          <Text size="sm" fw={600} style={{ color: '#92400e' }}>Before running in live mode:</Text>
          <Text size="sm" style={{ color: '#92400e', marginTop: 4 }}>
            1. Run dry-run first to confirm the count looks right.<br />
            2. Live mode writes to all tickets concurrently in pages of 200 — takes ~1–2 min for 5,000 tickets.<br />
            3. Safe to re-run — already-migrated tickets have empty TMETA values written over empty columns (no-op).
          </Text>
        </Paper>

        <Paper p="lg" withBorder radius="md">
          <Stack gap={16}>
            <Group gap={12}>
              <Button
                variant={dryRun ? 'filled' : 'light'}
                color={dryRun ? '#4B3FFF' : 'gray'}
                onClick={() => setDryRun(true)}
                size="sm">
                Dry Run (count only)
              </Button>
              <Button
                variant={!dryRun ? 'filled' : 'light'}
                color={!dryRun ? 'var(--color-error)' : 'gray'}
                onClick={() => setDryRun(false)}
                size="sm">
                Live (write to GAB)
              </Button>
            </Group>

            <Button
              size="md"
              loading={running}
              onClick={run}
              color={dryRun ? '#4B3FFF' : 'var(--color-error)'}
              leftSection={<IconDatabaseImport size={18} />}>
              {dryRun ? 'Run Dry Run' : 'Run Migration (LIVE)'}
            </Button>
          </Stack>
        </Paper>

        {result && (
          <Paper p="lg" withBorder radius="md">
            <Stack gap={12}>
              <Group gap={12}>
                <Badge size="lg" color="#4B3FFF">Total: {result.total.toLocaleString()}</Badge>
                <Badge size="lg" color="var(--color-success)">Migrated: {result.migrated.toLocaleString()}</Badge>
                <Badge size="lg" color="gray">Skipped: {result.skipped.toLocaleString()}</Badge>
                {result.errors > 0 && <Badge size="lg" color="var(--color-error)">Errors: {result.errors}</Badge>}
              </Group>

              {result.errors === 0 && !dryRun && (
                <Group gap={8} style={{ color: 'var(--color-success)' }}>
                  <IconCircleCheck size={20} />
                  <Text size="sm" fw={600}>Migration complete. TMETA data is now in real columns.</Text>
                </Group>
              )}

              {result.errors > 0 && (
                <Group gap={8} style={{ color: 'var(--color-error)' }}>
                  <IconAlertTriangle size={20} />
                  <Text size="sm" fw={600}>{result.errors} tickets failed — check the log below.</Text>
                </Group>
              )}

              {result.log.length > 0 && (
                <div style={{
                  background: '#0f172a', color: '#94a3b8', borderRadius: 8,
                  padding: '12px 16px', fontFamily: 'monospace', fontSize: '0.78rem',
                  maxHeight: 300, overflowY: 'auto',
                }}>
                  {result.log.map((line, i) => <div key={i}>{line}</div>)}
                </div>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
